import {
    Role,
    ApiData,
    RequestBody,
    RequestData,
    ClientError,
    AuthorizationData,
    JsonRequestResponse,
} from "./types"
import packageJs from "../package.json"

export default new class {
    userRoles: Role[]
    host: string
    token: string
    username: string
    userID: number
    tokenExpiryDate: string
    sessionAvailable: boolean
    interceptRequest: (req: RequestData) => RequestData
    interceptResponse: (req: RequestData, res: JsonRequestResponse) => JsonRequestResponse
    readonly STORAGE_KEY = 'ApiData'
    constructor() {
        const sessionData: ApiData = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY)||'{}')
        this.token = sessionData?.token
        this.host = localStorage.getItem('host')||'http://localhost:3000'
        this.username = sessionData?.username
        this.userID = parseInt(`${sessionData?.userID}`)
        this.tokenExpiryDate = sessionData?.tokenExpiryDate
        this.userRoles = JSON.parse(sessionData?.userRoles||'[]')
        this.sessionAvailable = Object.keys(sessionData).length > 0 && sessionData.token.length > 0
        this.interceptRequest = (req: RequestData) => req
        this.interceptResponse = (req, res: JsonRequestResponse) => res
    }

    getUsername() { return this.username }

    getUserID() { return this.userID }
    
    getHost() { return this.host }

    setHost(host: string) {
        this.host = host
        localStorage.setItem("host", this.host)
    }

    async restRequest(relativeUrl: string, data: RequestBody, baseUrl='api/v1'): Promise<JsonRequestResponse> {
        let res: undefined|JsonRequestResponse = undefined
        const requestPayload = {
            url: `${this.host}/${baseUrl}/${relativeUrl}`,
            config: {
                ...data,
                mode: 'cors',
                headers: {
                    'Authorization': this.token,
                    'Content-Type': 'application/json'
                }
            }
        }
        const interceptedRequestPayload: RequestData = this.interceptRequest(requestPayload)
        try {
            const req = await fetch(interceptedRequestPayload.url, interceptedRequestPayload.config)
            const statusResponse = req.status
            if ([200, 201].includes(statusResponse)) {
                const data = await req.json()
                return this.interceptResponse(interceptedRequestPayload, { ok: true, data })
            }
            switch(statusResponse) {
                case 400:
                    res = { ok: false, errorState: ClientError.BAD_REQUEST }
                    break;
                case 401:
                    res = { ok: false, errorState: ClientError.AUTHENTICATION_ERROR }
                    break;
                case 404:
                    res = { ok: false, errorState: ClientError.NOT_FOUND }
                    break;
                case 409: 
                    res = { ok: false, errorState: ClientError.RECORD_CONFLICT}
                    break;
                case 422:
                    res = { ok: false, errorState: ClientError.ENTITY_ERROR }
                    break;
                case 500:
                    res = { ok: false, errorState: ClientError.SERVER_ERROR }
                    break;
                case 502:
                    res = { ok: false, errorState: ClientError.GATE_WAY_ERROR }   
                    break; 
                default:
                    res = { ok: false, errorState: ClientError.GENERAL_REQUEST_ERROR }
                    break;
            }
            res.errorBody = await req.json()
        } catch(e) {
            console.error(`${e}`)
            if (/NetworkError|Failed to fetch/i.test(`${e}`)) {
                res = { ok: false, errorState: ClientError.NO_CONNECTION }
            } else {
                res = { ok: false, errorState: ClientError.INTERNAL_ERROR, errorBody: `${e}` }
            }
        }
        return this.interceptResponse(interceptedRequestPayload, res)
    }

    async apiVersionOk() {
        const apiVersion = await this.getVersion()
        // Remove the 'v' prefix if present
        const version = `${apiVersion}`.replace(/^v/, '');
        const minimumVersion = `${packageJs['min-api-version']}`.replace(/^v/, '');

        // Split the versions into arrays of numbers
        const versionParts = version.split('.');
        const minimumVersionParts = minimumVersion.split('.');

        // Compare each part of the version numbers
        for (let i = 0; i < Math.max(versionParts.length, minimumVersionParts.length); i++) {
            const versionPart = parseInt(versionParts[i] || '0');
            const minimumVersionPart = parseInt(minimumVersionParts[i] || '0');

            if (versionPart > minimumVersionPart) {
                return true;
            } else if (versionPart < minimumVersionPart) {
                return false;
            }
        }
        // All parts are equal
        return true;
    }

    async login(username: string, password: string) {
        if (!(await this.apiVersionOk())) {
            const version = await this.getVersion()
            return { 
                ok: false, 
                errorState: ClientError.INVALID_API_VERSION, 
                errorBody: {
                    errors: [`Current API version ${version} does not meet minimum required version of ${packageJs['min-api-version']}`]
                }
            }
        }
        this.sessionAvailable = false
        const res = await this.postJson('auth/login', { username, password })
        if (res.ok) {
            const data: AuthorizationData = res.data.authorization 
            this.tokenExpiryDate = data.expiry_time
            this.username = username
            this.userID = data.user.user_id
            this.userRoles = data.user.roles
            this.token = data.token
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify({
                host: this.host,
                username: this.username,
                token: this.token,
                userID: this.userID,
                tokenExpiryDate: this.tokenExpiryDate,
                userRoles: JSON.stringify(this.userRoles)
            }))
            this.sessionAvailable = true
        }
        return res
    }

    clearLogin() {
        this.token = ''
        this.userID = -1
        this.username = ''
        this.userRoles = []
        this.tokenExpiryDate = ''
        this.sessionAvailable = false
        sessionStorage.removeItem(this.STORAGE_KEY)
    }

    isLoggedIn() { return this.sessionAvailable }

    userHasRoles(roles: string[]) {
        const sanitize = (txt: string) => txt.toLowerCase().trim()
        const sanitizedRoles = roles.map(r => sanitize(r))
        return this.userRoles.some((role) => sanitizedRoles.includes(sanitize(role.role)))
    }

    getJson(url: string, params: Record<string, any>={}) {
        const urlParams = Object.keys(params).map(k => `${k}=${params[k]}`).join('&')
        const fullUrl = urlParams.length ? `${url}?${urlParams}` : url
        return this.restRequest(fullUrl, { method: "GET" })
    }

    postJson(url: string, data: any) {
        return this.restRequest(url, {
            method: "POST",
            body: JSON.stringify(data)
        })
    }

    putJson(url: string, data: any) {
        return this.restRequest(url, { 
            method: "PUT",
            body: JSON.stringify(data)
        })
    }

    void(url: string) { return this.restRequest(url, { method: "DELETE" }) }

    tokenHasExpired() { return this.tokenExpiryDate ? new Date() > new Date(this.tokenExpiryDate) : true }

    async apiOk() {
        const req = await this.getJson('_health')
        return req.ok && `${req?.data?.status}`.toLowerCase() === 'up'
    }

    async getDate() {
        const req = await this.getJson('current_time')
        if (req.ok) return req.data
    }

    async getVersion() {
        const req = await this.getJson('version')
        if (req.ok) return req.data['System version']
    }
}
